import { useEffect, useMemo, useState } from "react";
import { api, type Board as BoardData, type Container, type Task } from "../api.ts";
import { captionStyle, colors, serif, shortDate, tokens } from "../theme.ts";
import { Chip, GlassCard } from "../components/glass.tsx";
import { TaskRow } from "../components/TaskRow.tsx";
import { containerLine } from "../components/ContainerSheet.tsx";

/** Still Karim's to act on: not done, not parked. */
const isOpen = (t: Task) => t.state !== "done" && t.state !== "parked";
/** A container still in play: active or waiting. */
const isLive = (c: Container) => c.lifecycle === "active" || c.lifecycle === "waiting";

/** Appointments by time, then dated ascending, then the board's own order. Finished ones last. */
const byWhen = (a: Task, b: Task) => {
  const open = Number(isOpen(b)) - Number(isOpen(a));
  if (open) return open;
  const wa = a.at_local ?? a.due, wb = b.at_local ?? b.due;
  if (wa && wb && wa !== wb) return wa.localeCompare(wb);
  if (wa !== wb) return wa ? -1 : 1;
  return 0;
};

/**
 * Board (V3): each area, then its containers with their open tasks, then the
 * tasks loose in the area. Tapping a container's heading opens its detail.
 * Parked and done tasks, and closed and archived containers, are hidden
 * behind the one toggle. No counts.
 */
export function Board({
  version, onOpen, onOpenContainer,
}: { version: number; onOpen: (id: string) => void; onOpenContainer: (key: string) => void }) {
  const [data, setData] = useState<BoardData | null>(null);
  const [containers, setContainers] = useState<Container[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [all, setAll] = useState(false);

  // `version` changes when something is edited or the screen is pulled down.
  useEffect(() => {
    let live = true;
    Promise.all([api.board(), api.containers()])
      .then(([b, c]) => { if (live) { setData(b); setContainers(c); setError(null); } })
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [version]);

  const titleOf = useMemo(() => new Map((data?.tasks ?? []).map((t) => [t.id, t.title])), [data]);

  const groups = useMemo(() => {
    if (!data) return [];
    const tasks = data.tasks.filter((t) => all || isOpen(t));
    const shownContainers = containers.filter((c) => all || isLive(c));
    const shownIds = new Set(shownContainers.map((c) => c.id));
    const areaRows = [...data.areas].sort((a, b) => a.sort - b.sort)
      .map((a) => {
        const cs = shownContainers
          .filter((c) => c.area_id === a.id)
          .sort((x, y) => Number(isLive(y)) - Number(isLive(x)) || x.sort - y.sort || x.key.localeCompare(y.key))
          .map((c) => ({ container: c, tasks: tasks.filter((t) => t.container_id === c.id).sort(byWhen) }));
        // A task in a hidden container still shows, loose under its area, rather than vanishing.
        const loose = tasks.filter((t) => t.area_id === a.id && (!t.container_id || !shownIds.has(t.container_id))).sort(byWhen);
        return { id: a.id, name: a.name, key: a.key, containers: cs, loose };
      });
    const noArea = tasks.filter((t) => !t.area_id).sort(byWhen);
    if (noArea.length) areaRows.push({ id: "", name: "No area", key: null, containers: [], loose: noArea });
    return areaRows.filter((g) => g.containers.length > 0 || g.loose.length > 0);
  }, [data, containers, all]);

  const metaFor = (t: Task) => {
    if (t.state === "done") return "done";
    if (t.state === "parked") return t.parked_because ? `parked: ${t.parked_because}` : "parked";
    const bits: (string | null)[] = [t.needs_triage ? "to sort" : null];
    if (t.state === "waiting") bits.push(t.follow_up_on ? `waiting, chase ${shortDate(t.follow_up_on)}` : "waiting");
    else if (t.blocked_by.length) bits.push(`after ${t.blocked_by.map((id) => titleOf.get(id) ?? id).join(", ")}`);
    else if (t.blocked && t.blocked_until) bits.push(`not before ${shortDate(t.blocked_until)}`);
    else bits.push(t.money, t.mins ? `${t.mins} min` : null);
    return [t.id, ...bits].filter(Boolean).join(" · ");
  };

  const rightFor = (t: Task) => (t.at_local ? `${shortDate(t.at_local.slice(0, 10))}, ${t.at_local.slice(11, 16)}` : shortDate(t.due));

  const row = (t: Task) => (
    <TaskRow
      key={t.id} task={t} onOpen={() => onOpen(t.id)} right={rightFor(t)}
      meta={metaFor(t)} quiet={!isOpen(t) || t.blocked || t.state === "waiting"}
    />
  );

  return (
    <div style={{ padding: `12px ${tokens.space.gutter}px 0` }}>
      <div style={{
        marginBottom: 14, fontFamily: serif, fontSize: tokens.type.screenTitle.size,
        lineHeight: tokens.type.screenTitle.lineHeight, color: colors.ink,
      }}>Board</div>

      {error && <p style={{ margin: "0 0 12px", fontSize: 14, color: colors.ink }}>{error}</p>}
      {!data && !error && <p style={{ margin: 0, fontSize: 15, color: colors.ink }}>Opening…</p>}

      {data && (
        <>
          <Chip on={all} onClick={() => setAll(!all)}>Show parked, done and closed</Chip>

          {groups.length === 0 && (
            <GlassCard padding={16} style={{ marginTop: 14 }}>
              <p style={{ margin: 0, fontSize: 15, color: colors.ink2 }}>Nothing open.</p>
            </GlassCard>
          )}

          {groups.map((g) => (
            <GlassCard key={g.id || "none"} padding="14px 16px 2px" style={{ marginTop: 14 }}>
              <div style={captionStyle}>{g.key && g.key !== g.name.toUpperCase() ? `${g.name} · ${g.key}` : g.name}</div>

              {g.containers.map(({ container: c, tasks }) => (
                <div key={c.id} style={{ marginTop: 12 }}>
                  <div
                    role="button" tabIndex={0}
                    onClick={() => onOpenContainer(c.key)}
                    onKeyDown={(e) => { if (e.key === "Enter") onOpenContainer(c.key); }}
                    style={{ cursor: "pointer", paddingBottom: 4, lineHeight: 1.3 }}
                  >
                    <div style={{ fontSize: 15, fontWeight: 600, color: isLive(c) ? colors.ink : colors.ink2 }}>{c.name}</div>
                    <div style={{ fontSize: 12.5, color: colors.ink2, marginTop: 2 }}>{containerLine(c)}</div>
                  </div>
                  <div style={{ paddingLeft: 10, borderLeft: `1px solid ${colors.hair2}` }}>
                    {tasks.map(row)}
                  </div>
                </div>
              ))}

              {g.loose.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  {g.containers.length > 0 && <div style={{ fontSize: 12.5, color: colors.ink2 }}>Loose in {g.name}</div>}
                  {g.loose.map(row)}
                </div>
              )}
            </GlassCard>
          ))}
        </>
      )}
    </div>
  );
}
